
import { useState, useEffect } from 'react';

interface CaseStudy {
  id: number;
  title: string;
  client: string;
  challenge: string;
  solution: string;
  result: string;
  image: string;
  savings: string;
}

export default function VideoTestimonials() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCase, setActiveCase] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('case-studies');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const caseStudies: CaseStudy[] = [
    {
      id: 1,
      title: 'Small Business Success',
      client: 'Tech Startup with 25 Employees',
      challenge: 'A growing Dallas tech startup needed to offer competitive health benefits to attract talent but had limited budget and no HR experience with group insurance.',
      solution: 'We analyzed their workforce demographics, compared plans from 8 carriers, and designed a tiered benefits package that gave employees choice while controlling costs.',
      result: 'Implemented a comprehensive group plan with 95% employee participation. The company saved 18% compared to their initial quotes while offering better coverage options.',
      image: 'https://public.readdy.ai/ai/img_res/b3d938f83e1b10d399dbb8b2c0ba3bed.jpg',
      savings: '$42,000 Annual Savings'
    },
    {
      id: 2,
      title: 'Family Coverage Transformation',
      client: 'Family of Five in North Dallas',
      challenge: 'Parents with three children were paying $1,800/month for marketplace insurance with high deductibles and limited provider networks.',
      solution: 'We conducted a comprehensive needs analysis, reviewed their medical usage patterns, and identified a plan with their preferred pediatrician and specialists in-network.',
      result: 'Reduced monthly premiums to $1,250 with lower deductibles and better coverage. The family now has access to Children\'s Medical Center and all their preferred providers.',
      image: 'https://readdy.ai/api/search-image?query=happy-family-of-five-with-three-children-sitting-together-in-bright-modern-dallas-home-living-room-parents-and-kids-smiling-warmly-comfortable-contemporary-interior-natural-lighting-warm-atmosphere&width=800&height=600&seq=case-study-002&orientation=landscape',
      savings: '$6,600 Annual Savings'
    },
    {
      id: 3,
      title: 'Medicare Made Simple',
      client: 'Retiring Couple in Highland Park',
      challenge: 'Both spouses turning 65 within months of each other, overwhelmed by Medicare options, supplement plans, and prescription drug coverage decisions.',
      solution: 'We provided personalized Medicare education sessions, analyzed their prescription needs, compared Medigap vs. Medicare Advantage options, and coordinated enrollment timing.',
      result: 'Both enrolled in Medicare Supplement Plan G with standalone Part D coverage, ensuring comprehensive protection and freedom to see any Medicare-accepting doctor nationwide.',
      image: 'https://readdy.ai/api/search-image?query=elegant-senior-couple-in-their-mid-sixties-smiling-together-in-beautiful-upscale-dallas-home-bright-natural-lighting-comfortable-sophisticated-interior-warm-happy-retirement-atmosphere&width=800&height=600&seq=case-study-003&orientation=landscape',
      savings: 'Peace of Mind Priceless'
    }
  ];

  return (
    <section id="case-studies" className="py-20 bg-gradient-to-b from-lightBlue/5 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            Client Success Stories
          </h2>
          <p className="text-lg text-darkGray max-w-3xl mx-auto">
            Real challenges solved with expert guidance and personalized insurance solutions
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-1/3 space-y-4">
            {caseStudies.map((study, index) => (
              <button
                key={study.id}
                onClick={() => setActiveCase(index)}
                className={`w-full text-left p-6 rounded-xl transition-all duration-300 cursor-pointer ${
                  activeCase === index
                    ? 'bg-secondary text-white shadow-lg'
                    : 'bg-white border border-lightBlue/20 hover:border-secondary/30'
                }`}
              >
                <h3 className={`font-semibold text-lg mb-2 ${
                  activeCase === index ? 'text-white' : 'text-primary'
                }`}>
                  {study.title}
                </h3>
                <p className={`text-sm mb-3 ${
                  activeCase === index ? 'text-white/90' : 'text-darkGray'
                }`}>
                  {study.client}
                </p>
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                  activeCase === index
                    ? 'bg-white/20 text-white'
                    : 'bg-secondary/10 text-secondary'
                }`}>
                  <i className="ri-check-line"></i>
                  <span>{study.savings}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="lg:w-2/3">
            <div
              className={`bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-700 ${
                isVisible ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
              }`}
            >
              <div className="relative h-80">
                <img
                  src={caseStudies[activeCase].image}
                  alt={caseStudies[activeCase].title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="font-heading text-3xl font-bold text-white mb-2">
                    {caseStudies[activeCase].title}
                  </h3>
                  <p className="text-white/90 text-lg">
                    {caseStudies[activeCase].client}
                  </p>
                </div>
              </div>

              <div className="p-8 space-y-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                      <i className="ri-error-warning-line text-xl text-red-500"></i>
                    </div>
                    <h4 className="font-semibold text-lg text-primary">The Challenge</h4>
                  </div>
                  <p className="text-darkGray leading-relaxed pl-13">
                    {caseStudies[activeCase].challenge}
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <i className="ri-lightbulb-line text-xl text-blue-500"></i>
                    </div>
                    <h4 className="font-semibold text-lg text-primary">Our Solution</h4>
                  </div>
                  <p className="text-darkGray leading-relaxed pl-13">
                    {caseStudies[activeCase].solution}
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                      <i className="ri-trophy-line text-xl text-secondary"></i>
                    </div>
                    <h4 className="font-semibold text-lg text-primary">The Result</h4>
                  </div>
                  <p className="text-darkGray leading-relaxed pl-13">
                    {caseStudies[activeCase].result}
                  </p>
                </div>

                <div className="pt-6 border-t border-lightBlue/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <i className="ri-money-dollar-circle-line text-3xl text-secondary"></i>
                      <div>
                        <div className="text-sm text-darkGray">Client Savings</div>
                        <div className="text-xl font-bold text-secondary">
                          {caseStudies[activeCase].savings}
                        </div>
                      </div>
                    </div>
                    <a
                      href="tel:+12143060121"
                      className="px-6 py-3 bg-secondary text-white rounded-lg font-semibold hover:bg-secondary/90 transition-all whitespace-nowrap cursor-pointer"
                    >
                      Get Your Free Quote
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
